import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Fab from '@material-ui/core/Fab';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import {
  fetchApiRef,
  identityApiRef,
  storageApiRef,
  useAnalytics,
  useApi,
} from '@backstage/frontend-plugin-api';
import { starredEntitiesApiRef } from '@backstage/plugin-catalog-react';
import { Joyride, STATUS, type EventData, type Step } from 'react-joyride';
import { Checklist } from './Checklist';
import { GUIDES, isReady, type Guide } from './guides';
import {
  type ChecklistItem,
  type OnboardingState,
  isItemDone,
  markGuideDone,
  markItemDone,
  STORAGE_BUCKET,
  STORAGE_KEY,
  updateState,
  visibleItems,
} from './progress';
import { tourSteps } from './steps';
import { TourTooltip } from './TourTooltip';
import {
  isReleaseNotes,
  showLauncher,
  updateWhatsNew,
  WHATS_NEW_KEY,
  whatsNewSteps,
  type ReleaseNotes,
  type WhatsNewState,
} from './whatsNew';
import { WhatsNewDialog, WhatsNewLauncher } from './WhatsNewDialog';

/** A value of the user's `onboarding` storage bucket; undefined while it loads. */
function useStored<T extends object>(key: string): T | undefined {
  const bucket = useApi(storageApiRef).forBucket(STORAGE_BUCKET);
  const [snapshot, setSnapshot] = useState(() => bucket.snapshot<T>(key));
  useEffect(() => {
    const sub = bucket.observe$<T>(key).subscribe(setSnapshot);
    return () => sub.unsubscribe();
  }, [bucket, key]);
  if (snapshot.presence === 'unknown') return undefined;
  return snapshot.value ?? ({} as T);
}

/** `/release-notes.json`, baked into the image at build time; absent locally and before a release. */
function useReleaseNotes(): ReleaseNotes | undefined {
  const [notes, setNotes] = useState<ReleaseNotes>();
  useEffect(() => {
    fetch('/release-notes.json', { cache: 'no-cache' })
      .then(res => (res.ok ? res.json() : undefined))
      .then(data => isReleaseNotes(data) && setNotes(data))
      .catch(() => {});
  }, []);
  return notes;
}

const WELCOME = 'welcome';
const WHATS_NEW = 'whats-new';

interface Running {
  id: string;
  steps: Step[];
  key: number;
  inDrawer?: boolean;
}

const useStyles = makeStyles(theme => ({
  dock: {
    position: 'fixed',
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1.5),
    // Above the page, under dialogs and drawers.
    zIndex: theme.zIndex.appBar,
  },
  help: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
  },
}));

/**
 * First-visit onboarding: the welcome tour (react-joyride), the « Premiers pas » checklist, and
 * a how-to guide per page or drawer (guides.tsx) with a « ? » button to see it again. Progress
 * lives in the user's storage (bucket `onboarding`); analytics events complete items (index.tsx).
 */
export function Onboarding() {
  const storageApi = useApi(storageApiRef);
  const identityApi = useApi(identityApiRef);
  const fetchApi = useApi(fetchApiRef);
  const starredApi = useApi(starredEntitiesApiRef);
  const analytics = useAnalytics();
  const navigate = useNavigate();
  const theme = useTheme();
  const state = useStored<OnboardingState>(STORAGE_KEY);
  const whatsNew = useStored<WhatsNewState>(WHATS_NEW_KEY);
  const notes = useReleaseNotes();
  const [notesOpen, setNotesOpen] = useState(false);

  const classes = useStyles();
  const [firstName, setFirstName] = useState<string>();
  const [roles, setRoles] = useState<string[]>([]);
  const [githubLinked, setGithubLinked] = useState(false);
  const [hasStarred, setHasStarred] = useState(false);
  // The welcome tour or the page guide on screen; a new key restarts Joyride from step one.
  const [running, setRunning] = useState<Running>();
  // The guide that applies to what is on screen, if any.
  const [available, setAvailable] = useState<Guide>();
  // Its first target is on screen (the page has loaded its data).
  const [ready, setReady] = useState(false);
  // Ended in this session: storage updates are async, so this keeps them from starting again.
  const ended = useRef(new Set<string>());

  useEffect(() => {
    Promise.all([
      identityApi.getBackstageIdentity(),
      identityApi.getProfileInfo(),
    ])
      .then(([identity, profile]) => {
        setFirstName(profile.displayName?.trim().split(/\s+/)[0]);
        setRoles(
          identity.ownershipEntityRefs
            .filter(ref => ref.startsWith('role:'))
            .map(ref => ref.slice(ref.indexOf('/') + 1)),
        );
      })
      .catch(() => {});
    fetchApi
      .fetch('plugin://platform-orchestrator/me/github')
      .then(res => (res.ok ? res.json() : undefined))
      .then(me => setGithubLinked(Boolean(me?.login)))
      .catch(() => {});
    const sub = starredApi
      .starredEntitie$()
      .subscribe(starred => setHasStarred(starred.size > 0));
    return () => sub.unsubscribe();
  }, [identityApi, fetchApi, starredApi]);

  // Drawers open without a route change: look at the screen every second.
  useEffect(() => {
    const check = () => {
      const guide = GUIDES.find(g => g.appliesTo(window.location.pathname));
      setAvailable(guide);
      setReady(guide ? isReady(guide) : false);
    };
    check();
    const id = window.setInterval(check, 1000);
    return () => window.clearInterval(id);
  }, []);

  const start = useCallback((id: string, steps: Step[], inDrawer?: boolean) => {
    ended.current.delete(id);
    setRunning({ id, steps, inDrawer, key: Date.now() });
  }, []);

  // The release notes of a new version, once per user. Newcomers get the welcome tour instead.
  useEffect(() => {
    if (!notes || !state || !whatsNew || whatsNew.seenVersion === notes.version)
      return;
    if (!state.tourDone) {
      updateWhatsNew(storageApi, { seenVersion: notes.version }).catch(
        () => {},
      );
    } else if (!running && !ended.current.has(`${WHATS_NEW}-dialog`)) {
      setNotesOpen(true);
    }
  }, [notes, state, whatsNew, running, storageApi]);

  const closeNotes = useCallback(() => {
    setNotesOpen(false);
    ended.current.add(`${WHATS_NEW}-dialog`);
    if (notes) {
      analytics.captureEvent('whats-new', notes.version);
      updateWhatsNew(storageApi, { seenVersion: notes.version }).catch(
        () => {},
      );
    }
  }, [notes, analytics, storageApi]);

  // The welcome tour first, then each guide the first time its page or drawer shows up.
  useEffect(() => {
    if (!state || running || notesOpen) return;
    if (!state.tourDone) {
      if (!ended.current.has(WELCOME)) start(WELCOME, tourSteps(firstName));
    } else if (
      available &&
      ready &&
      !state.guidesDone?.includes(available.id) &&
      !ended.current.has(available.id)
    ) {
      start(available.id, available.steps, available.inDrawer);
    }
  }, [state, running, notesOpen, available, ready, firstName, start]);

  const end = useCallback(
    (id: string, status: string, step: number) => {
      // A click that finishes a guide can also take its page away: count it once.
      if (ended.current.has(id)) return;
      ended.current.add(id);
      setRunning(undefined);
      if (id === WELCOME) {
        analytics.captureEvent('onboarding-tour', status, { value: step });
        updateState(storageApi, { tourDone: true }).catch(() => {});
      } else if (id === WHATS_NEW) {
        analytics.captureEvent('whats-new-tour', status, { value: step });
        if (notes)
          updateWhatsNew(storageApi, {
            seenVersion: notes.version,
            tourVersion: notes.version,
          }).catch(() => {});
      } else {
        analytics.captureEvent('onboarding-guide', id, {
          value: step,
          attributes: { status },
        });
        markGuideDone(storageApi, id).catch(() => {});
      }
    },
    [analytics, storageApi, notes],
  );

  // A guide whose page or drawer went away stops, and counts as seen.
  useEffect(() => {
    if (
      running &&
      running.id !== WELCOME &&
      running.id !== WHATS_NEW &&
      available?.id !== running.id
    )
      end(running.id, 'left', 0);
  }, [running, available, end]);

  const onEvent = useCallback(
    (data: EventData) => {
      if (!running) return;
      if (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED)
        end(running.id, data.status, data.index + 1);
    },
    [running, end],
  );

  const facts = { roles, githubLinked, hasStarred };
  const items = visibleItems(facts).map(item => ({
    ...item,
    done: state ? isItemDone(item, state, facts) : false,
  }));
  const showChecklist =
    state?.tourDone &&
    !running &&
    !state.checklistHidden &&
    items.some(item => !item.done);
  // Drawer guides have no button: it would sit under the drawer.
  const showHelp =
    state?.tourDone && !running && available && !available.inDrawer;

  const openItem = (item: ChecklistItem) => {
    if (item.completeOnClick) markItemDone(storageApi, item.id).catch(() => {});
    navigate(item.href);
  };

  const showWhatsNew =
    notes &&
    whatsNew &&
    state?.tourDone &&
    !running &&
    !notesOpen &&
    showLauncher(notes, whatsNew);

  return (
    <>
      {notes && (
        <WhatsNewDialog
          notes={notes}
          open={notesOpen}
          onClose={closeNotes}
          onTour={() => {
            closeNotes();
            start(WHATS_NEW, whatsNewSteps(notes));
          }}
        />
      )}
      {showWhatsNew && (
        <WhatsNewLauncher notes={notes} onClick={() => setNotesOpen(true)} />
      )}
      {running && (
        <Joyride
          key={running.key}
          run
          steps={running.steps}
          continuous
          tooltipComponent={TourTooltip}
          options={{
            primaryColor: theme.palette.primary.main,
            backgroundColor: theme.palette.background.paper,
            textColor: theme.palette.text.primary,
            overlayColor: 'rgba(16, 24, 40, 0.45)',
            spotlightRadius: 8,
            skipBeacon: true,
            // The cross ends the tour instead of moving to the next step.
            closeButtonAction: 'skip',
            overlayClickAction: false,
            // Above the drawers the guides explain: MUI drawers are modals (zIndex.modal).
            zIndex: theme.zIndex.modal + 1,
            // A drawer already traps the focus; two traps would fight over it.
            disableFocusTrap: running.inDrawer,
          }}
          onEvent={onEvent}
        />
      )}
      {(showChecklist || showHelp) && (
        <div className={classes.dock}>
          {showChecklist && (
            <Checklist
              items={items}
              onOpenItem={openItem}
              onReplayTour={() => {
                ended.current.delete(WELCOME);
                start(WELCOME, tourSteps(firstName));
              }}
              onHide={() =>
                updateState(storageApi, { checklistHidden: true }).catch(
                  () => {},
                )
              }
            />
          )}
          {showHelp && (
            <Tooltip title="Guide de la page">
              <Fab
                size="small"
                aria-label="Guide de la page"
                className={classes.help}
                onClick={() =>
                  start(available.id, available.steps, available.inDrawer)
                }
              >
                <HelpOutlineIcon />
              </Fab>
            </Tooltip>
          )}
        </div>
      )}
    </>
  );
}
